// Premium Sound Notification System using Web Audio API
// Generates elegant, non-irritating tones programmatically

const SETTINGS_KEY = 'lunora_sound_settings';

const defaultSettings = {
  soundEnabled: true,
  volume: 0.6,
  vibrationEnabled: true,
  nightMode: false,
  urgentOnly: false,
};

export const loadSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  } catch {
    return defaultSettings;
  }
};

export const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
};

let audioCtx = null;

const getAudioContext = () => {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

const createGain = (ctx, volume, startTime, attackTime, sustainTime, releaseTime) => {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + attackTime);
  gain.gain.setValueAtTime(volume, startTime + attackTime + sustainTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + attackTime + sustainTime + releaseTime);
  return gain;
};

// 1. New Order — Soft luxury chime (two harmonious bell tones)
export const playNewOrderSound = (volume = 0.6) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.value = volume;
    masterGain.connect(ctx.destination);

    // First chime — C6
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 1047;
    const g1 = createGain(ctx, 0.35, now, 0.01, 0.08, 0.8);
    osc1.connect(g1);
    g1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.9);

    // Second chime — E6 (major third)
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 1319;
    const g2 = createGain(ctx, 0.3, now + 0.15, 0.01, 0.08, 0.7);
    osc2.connect(g2);
    g2.connect(masterGain);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.95);

    // Third chime — G6 (perfect fifth)
    const osc3 = ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.value = 1568;
    const g3 = createGain(ctx, 0.2, now + 0.3, 0.01, 0.06, 0.6);
    osc3.connect(g3);
    g3.connect(masterGain);
    osc3.start(now + 0.3);
    osc3.stop(now + 0.96);
  } catch {}
};

// 2. Urgent Order — Premium priority alert (rising tones with emphasis)
export const playUrgentOrderSound = (volume = 0.6) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.value = volume;
    masterGain.connect(ctx.destination);

    // First pulse — A5
    const osc1 = ctx.createOscillator();
    osc1.type = 'triangle';
    osc1.frequency.value = 880;
    const g1 = createGain(ctx, 0.4, now, 0.005, 0.1, 0.15);
    osc1.connect(g1);
    g1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.28);

    // Second pulse — C#6 (tritone tension)
    const osc2 = ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = 1109;
    const g2 = createGain(ctx, 0.4, now + 0.2, 0.005, 0.1, 0.15);
    osc2.connect(g2);
    g2.connect(masterGain);
    osc2.start(now + 0.2);
    osc2.stop(now + 0.48);

    // Third pulse — E6 (resolution)
    const osc3 = ctx.createOscillator();
    osc3.type = 'triangle';
    osc3.frequency.value = 1319;
    const g3 = createGain(ctx, 0.45, now + 0.4, 0.005, 0.12, 0.4);
    osc3.connect(g3);
    g3.connect(masterGain);
    osc3.start(now + 0.4);
    osc3.stop(now + 0.95);

    // Sub-bass impact
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 110;
    const gs = createGain(ctx, 0.15, now, 0.005, 0.05, 0.3);
    sub.connect(gs);
    gs.connect(masterGain);
    sub.start(now);
    sub.stop(now + 0.36);
  } catch {}
};

// 3. Payment Received — Success tone (warm ascending chord)
export const playPaymentSound = (volume = 0.6) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.value = volume;
    masterGain.connect(ctx.destination);

    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const g = createGain(ctx, 0.25 - i * 0.03, now + i * 0.1, 0.01, 0.15, 0.5 + i * 0.1);
      osc.connect(g);
      g.connect(masterGain);
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.7 + i * 0.1);
    });
  } catch {}
};

// 4. Overdue Reminder — Soft pulse alert (gentle two-note descent)
export const playOverdueSound = (volume = 0.6) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.value = volume;
    masterGain.connect(ctx.destination);

    // High note — G5
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 784;
    const g1 = createGain(ctx, 0.25, now, 0.02, 0.2, 0.4);
    osc1.connect(g1);
    g1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.65);

    // Low note — E5 (minor third down, melancholic)
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 659;
    const g2 = createGain(ctx, 0.2, now + 0.35, 0.02, 0.2, 0.5);
    osc2.connect(g2);
    g2.connect(masterGain);
    osc2.start(now + 0.35);
    osc2.stop(now + 1.1);
  } catch {}
};

// Vibrate helper
export const triggerVibration = (pattern) => {
  const settings = loadSettings();
  if (settings.vibrationEnabled && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

// Play sound based on type with settings check
export const playNotificationSound = (type) => {
  const settings = loadSettings();
  if (!settings.soundEnabled) return;
  if (settings.nightMode) return;
  if (settings.urgentOnly && type !== 'urgent') return;

  const vol = settings.volume;

  switch (type) {
    case 'new-order':
      playNewOrderSound(vol);
      triggerVibration([150, 80, 150]);
      break;
    case 'urgent':
      playUrgentOrderSound(vol);
      triggerVibration([200, 100, 200, 100, 300]);
      break;
    case 'payment':
      playPaymentSound(vol);
      triggerVibration([100, 50, 100]);
      break;
    case 'overdue':
      playOverdueSound(vol);
      triggerVibration([200, 200, 200]);
      break;
    default:
      playNewOrderSound(vol);
  }
};
